import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {createAddress, getAddressesSuggestList, getAddressInfoFromString} from 'api/geo';
import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {AsyncSelect, Select, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {editParticipantData$, updateParticipantFx} from 'models/profile/common';
import {ParticipantRegistrationRequest, ParticipantUpdateRequest} from 'types';
import {Address} from 'types/geo';
import {formatAddressInfoToRequest} from 'utils/address';
import {mapStorageTypesToI18nOptions} from 'utils/select';

import {Actions, BackButton, CustomTitle, Field, FormFields, SubmitButton, Wrapper} from './styled';

type FormField = {
    name: keyof ParticipantRegistrationRequest;
    readonly?: boolean;
    options?: SelectOption<string | number>[];
};

export const UpdateParticipant = () => {
    const {t, i18n} = useTranslation();
    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language, t],
    );
    const translateForm = useCallback(
        (key: string) => t(`pages.profile.participant.fields.${key}`),
        [i18n.language],
    );

    const editParticipantData = useStore(editParticipantData$);
    useEffect(() => {
        if (!editParticipantData) return history.push(ROUTE.PROFILE);

        setValue('storageGln', editParticipantData.storageGln || '');
        setValue('storageTypeId', editParticipantData.storageType.id);
        setValue('storageName', editParticipantData.storageName || '');
        setValue('storageAddress', editParticipantData.address.address);
    }, []);

    const schema = useMemo(
        () =>
            yup.object().shape({
                storageName: yup.string().required(translateValidates('required')),
                storageGln: yup.string().required(translateValidates('required')),
                storageTypeId: yup
                    .number()
                    .typeError(translateValidates('required'))
                    .required(translateValidates('required')),
                storageAddress: yup.string().required(translateValidates('required')),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
        reset,
    } = useForm<ParticipantRegistrationRequest>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const onSubmit = async (data: ParticipantRegistrationRequest) => {
        const {storageAddress} = data;
        const storageAddressId = await getAddressId(storageAddress);
        if (!storageAddressId) throw Error('storageAddressId is required');
        data.storageAddressId = storageAddressId;

        try {
            if (!editParticipantData?.id) return;
            const payload: ParticipantUpdateRequest & {id: string | number} = {
                gln: {value: data.storageGln, reset: false},
                storageTypeId: {value: data.storageTypeId, reset: false},
                storageName: {value: data.storageName || '', reset: false},
                addressId: {value: data.storageAddressId, reset: false},
                id: editParticipantData.id,
            };

            await updateParticipantFx(payload);
            reset();
            history.push(ROUTE.PROFILE);
        } catch (error) {
            console.error(error);
        }
    };

    const formFields: FormField[] = useMemo(
        () => [
            {name: 'storageName'},
            {name: 'storageGln'},
            {
                name: 'storageTypeId',
                options: mapStorageTypesToI18nOptions(
                    new Array(12).fill(0).map((_, i) => ++i),
                    'dictionaries.storageType',
                ),
            },
            {name: 'storageAddress'},
        ],
        [t],
    );

    const addressOptions = async (partialAddress: string) => {
        try {
            if (!partialAddress) return [{value: '', label: ''}];
            const lang =
                (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) || 'ru';
            const payload: Address = {
                address: partialAddress,
                lang,
            };

            const values = (await getAddressesSuggestList(payload)).data;
            return values.map((value) => ({value, label: value}));
        } catch (error) {
            console.error(error);
            return [{value: '', label: ''}];
        }
    };

    const getAddressId = async (address?: string) => {
        if (!address) return;
        try {
            const addressPayload = (partialAddress: string): Address => {
                const lang =
                    (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) ||
                    'ru';
                return {
                    address: partialAddress,
                    lang,
                };
            };

            const payload = addressPayload(address);
            return await getAddressInfoFromString(payload)
                .then(formatAddressInfoToRequest)
                .then(createAddress);
        } catch (error) {
            console.error(error);
        }
    };

    const controlRender = (
        controlEvents: ControllerRenderProps<ParticipantRegistrationRequest>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
        };

        const props = {...baseProps, ...controlEvents};

        if (field.name === 'storageTypeId') {
            return (
                <div style={{paddingBottom: '6px'}}>
                    <Select
                        {...baseProps}
                        value={field.options?.find((o) => o.value === controlEvents.value) ?? null}
                        options={field.options}
                        onChange={(option: SelectOption<number>) =>
                            controlEvents.onChange(option.value)
                        }
                    />
                </div>
            );
        }

        if (field.name === 'storageAddress') {
            return (
                <div style={{paddingBottom: '6px'}}>
                    <AsyncSelect
                        {...baseProps}
                        menuPlacement="top"
                        name={controlEvents.name}
                        loadOptions={addressOptions}
                        isClearable
                        onChange={(v?: {value: string; label: string}) => {
                            controlEvents.onChange(v?.value || '');
                        }}
                        value={
                            controlEvents.value
                                ? {value: controlEvents.value, label: controlEvents.value}
                                : null
                        }
                    />
                </div>
            );
        }

        return <Input {...props} />;
    };

    return (
        <>
            <BackTo to={ROUTE.PROFILE}>{t('pages.profile.participant.buttons.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form onSubmit={handleSubmit(onSubmit)} style={{marginTop: 50}}>
                        <CustomTitle size={4}>{t('pages.profile.participant.title')}</CustomTitle>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>
                                        {t(`pages.profile.participant.fields.${field.name}`)}
                                    </span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <Actions>
                            <BackButton
                                type="button"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(ROUTE.STAFF);
                                    reset();
                                }}
                            >
                                {t('pages.profile.staff.create.cancel')}
                            </BackButton>
                            <SubmitButton type="submit">
                                {t('pages.profile.staff.update.updateBtn')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
        </>
    );
};
